import { Box, Button, Stack, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import history from "utils/history";
import viewpaths from "global/constants/ViewPathConstants";
import { useAppDispatch } from "hooks/use-app-dispatch";
import { useAppSelector } from "hooks/use-app-select";
import { selectAuthenticated, selectLastVisitedLink } from "redux/authSlice";
import {
  openErrorNotification,
  openSuccessNotification,
  openWarningNotification,
} from "helpers/methods";
import { exchangeSsoCode } from "./AuthService";
import AuthLayout from "layouts/AuthLayout/AuthLayout";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import {
  clearSsoCodeExchanged,
  clearSsoRequestedEmail,
  consumeSsoLogoutRedirect,
  establishSession,
  getSsoRequestedEmail,
  markSsoCodeExchanged,
  switchSsoAccount,
  wasSsoCodeExchanged,
} from "./authSession";

interface PendingSsoLogin {
  user: any;
  requestedEmail: string;
  returnedEmail: string;
}

const SsoCallback = () => {
  const dispatch = useAppDispatch();
  const current = useAppSelector(selectLastVisitedLink);
  const isAuthenticated = useAppSelector(selectAuthenticated);
  const search = useLocation().search;
  const handled = useRef(false);
  const [pendingLogin, setPendingLogin] = useState<PendingSsoLogin | null>(
    null,
  );
  const [switchingAccount, setSwitchingAccount] = useState(false);
  const [isSsoLogoutReturn] = useState(() => consumeSsoLogoutRedirect());

  const failToLogin = (message: string) => {
    openErrorNotification(message);
    history.replace(viewpaths.loginViewPath);
  };

  const finalizeLogin = (user: any) => {
    clearSsoRequestedEmail();
    establishSession(user, dispatch, current, undefined, "sso");
  };

  const checkEmailAndProceed = (user: any, returnedEmail: string) => {
    const requestedEmail = getSsoRequestedEmail();
    const normalizedRequested = requestedEmail?.trim().toLowerCase();
    const normalizedReturned = (returnedEmail ?? "").trim().toLowerCase();

    if (
      normalizedRequested &&
      normalizedReturned &&
      normalizedRequested !== normalizedReturned
    ) {
      setPendingLogin({
        user,
        requestedEmail: requestedEmail as string,
        returnedEmail,
      });
      return;
    }
    finalizeLogin(user);
  };

  const handleContinueWithReturnedAccount = () => {
    if (!pendingLogin) return;
    const { user } = pendingLogin;
    setPendingLogin(null);
    finalizeLogin(user);
  };

  const handleSignInDifferently = async () => {
    if (!pendingLogin) return;
    setSwitchingAccount(true);
    try {
      await switchSsoAccount(
        pendingLogin.requestedEmail,
        pendingLogin.user.authToken,
      );
    } finally {
      setSwitchingAccount(false);
    }
  };

  /**
   * The callback carries a single-use code; the logged-in user (same shape POST /user/login
   * returns) is fetched here over POST so it never lands in a URL, the browser's history, a
   * Referer header, or an ALB/proxy access log.
   */
  const exchangeCodeForToken = async (code: string) => {
    let user: any;
    try {
      user = await exchangeSsoCode(code);
    } catch (error: any) {
      clearSsoCodeExchanged();
      failToLogin(
        error?.message ??
          "Could not complete single sign-on. Please try signing in again.",
      );
      return;
    }
    checkEmailAndProceed(user, user.email);
  };

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    const params = new URLSearchParams(search);
    const error = params.get("error");
    const code = params.get("code");
    const message = params.get("message");

    // What arrives here is a single-use code, not the access token -- the token is fetched over
    // POST below. The code is still scrubbed from the address bar so a spent one does not linger
    // in history or in the Referer of anything this page loads. replaceState rather than the
    // router, so it happens synchronously without a re-render.
    if (search) {
      window.history.replaceState(null, "", window.location.pathname);
    }

    if (error) {
      if (isSsoLogoutReturn) {
        openWarningNotification(
          error === "slo_timeout"
            ? "You've been signed out of Inkfree. Your organization's SSO didn't confirm sign-out in time — you may need to sign out there separately."
            : "You've been signed out of Inkfree. We couldn't confirm sign-out from your organization's SSO — you may need to sign out there separately.",
        );
        history.replace(viewpaths.loginViewPath);
        return;
      }
      failToLogin("Single sign-on failed. Please try signing in again.");
      return;
    }
    if (!code) {
      if (message) {
        openSuccessNotification(message);
        history.replace(viewpaths.loginViewPath);
        return;
      }
      failToLogin("No single sign-on session was returned. Please try again.");
      return;
    }

    if (wasSsoCodeExchanged(code)) {
      if (isAuthenticated) {
        history.replace(current ?? viewpaths.dashboardViewPath);
      }
      return;
    }
    markSsoCodeExchanged(code);
    exchangeCodeForToken(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (pendingLogin) {
    return (
      <AuthLayout>
        <Box sx={{ textAlign: "center", py: 6, px: 2 }}>
          <Stack spacing={2} alignItems="center">
            <Typography variant="h6" color="text.primary" fontWeight={700}>
              You're signed in as a different account
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ maxWidth: 420 }}
            >
              You entered <b>{pendingLogin.requestedEmail}</b> to sign in, but
              your organization's identity provider authenticated you as{" "}
              <b>{pendingLogin.returnedEmail}</b>.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Do you want to continue signing in as{" "}
              <b>{pendingLogin.returnedEmail}</b>, or sign in with a different
              account?
            </Typography>
            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
              <Button
                variant="outlined"
                onClick={handleSignInDifferently}
                disabled={switchingAccount}
                aria-label="Sign in with a different account"
              >
                {switchingAccount ? "Redirecting..." : "Sign in with different account"}
              </Button>
              <Button
                variant="contained"
                onClick={handleContinueWithReturnedAccount}
                disabled={switchingAccount}
                aria-label={`Continue signing in as ${pendingLogin.returnedEmail}`}
              >
                Continue as {pendingLogin.returnedEmail}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <CustomLoader isLoading={true} />
      <Box sx={{ textAlign: "center", py: 6 }}>
        <Stack spacing={1} alignItems="center">
          <Typography variant="h6" color="text.primary" fontWeight={700}>
            {isSsoLogoutReturn ? "Signing you out…" : "Signing you in…"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isSsoLogoutReturn
              ? "Completing sign-out with your organization, please wait."
              : "Completing single sign-on, please wait."}
          </Typography>
        </Stack>
      </Box>
    </AuthLayout>
  );
};

export default SsoCallback;
